import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { Wrench, ChevronDown, Minimize2 } from "lucide-react";

interface TemplateDevAutofillProps {
  editingTemplate: any;
  setEditingTemplate: (updated: any) => void;
  setTemplateName: (name: string) => void;
  schemaEditor: any;
}

const TEMPLATE_AUTOFILL_SAMPLES = [
  {
    name: "1. Web App & API Pentest (PTaaS Standard)",
    templateName: "Web Application & API Pentest (PTaaS Standard)",
    schema: {
      sections: [
        {
          id: "sec_web_basics",
          title: "1. Client Stakeholders & Scope Profile",
          description: "Primary target environment coordinates and compliance objectives.",
          questions: [
            {
              qid: "org_legal_name",
              label: "Organization legal name",
              type: "short_text",
              required: true
            },
            {
              qid: "security_lead_email",
              label: "Security operations lead contact email",
              type: "email",
              required: true
            },
            {
              qid: "compliance_purpose",
              label: "Primary compliance standard driving this assessment",
              type: "single_choice",
              required: true,
              options: [
                { value: "pci_dss", label: "PCI-DSS Level 1 Audit Readiness" },
                { value: "soc2_type2", label: "SOC2 Type II Attestation Baseline" },
                { value: "hipaa", label: "HIPAA Security Rule (ePHI Compliance)" },
                { value: "iso27001", label: "ISO/IEC 27001 Information Security Management" },
                { value: "fedramp", label: "FedRAMP Moderate/High Authorization" },
                { value: "standard_governance", label: "Standard Corporate Best Practices / Internal SLA" }
              ]
            },
            {
              qid: "scope_criticality",
              label: "Business criticality tier of the systems in scope",
              type: "dropdown",
              required: true,
              options: [
                { value: "tier_critical", label: "Critical - processes financial/personal data or key system rails" },
                { value: "tier_operational", label: "Operational - system failure impacts daily business activities" },
                { value: "tier_low", label: "Low - non-production staging systems or informational utilities" }
              ]
            }
          ]
        },
        {
          id: "sec_web_tech",
          title: "2. Application Stack & Architecture",
          description: "Identify frontend frameworks, API structures, databases, and microservices.",
          questions: [
            {
              qid: "frontend_tech",
              label: "Frontend architecture & SPA frameworks",
              type: "multi_choice",
              required: true,
              options: [
                { value: "react_nextjs", label: "React / Next.js SPA/SSR Framework" },
                { value: "vue_nuxt", label: "Vue.js / Nuxt.js Client SPA" },
                { value: "angular", label: "Angular Enterprise Client SPA" },
                { value: "sveltekit", label: "Svelte / SvelteKit Application" },
                { value: "legacy_server_rendered", label: "Legacy Server-Rendered (Django, Laravel, Rails, ASP.NET, PHP)" },
                { value: "static_html", label: "Static HTML / Jekyll / Hugo Pages" }
              ]
            },
            {
              qid: "api_protocols",
              label: "Underlying API service protocols & schemas",
              type: "multi_choice",
              required: true,
              options: [
                { value: "rest_json", label: "RESTful JSON web services" },
                { value: "graphql_api", label: "GraphQL server endpoints" },
                { value: "grpc_protobuf", label: "gRPC / Protobuf microservice communication" },
                { value: "soap_xml", label: "SOAP XML legacy web services" },
                { value: "websocket_realtime", label: "WebSocket real-time connection endpoints" }
              ]
            },
            {
              qid: "database_storage",
              label: "Primary database and data storage engines",
              type: "multi_choice",
              required: true,
              options: [
                { value: "postgresql", label: "PostgreSQL relational database" },
                { value: "mongodb", label: "MongoDB NoSQL document store" },
                { value: "redis", label: "Redis in-memory caching" },
                { value: "mysql", label: "MySQL / MariaDB database" },
                { value: "dynamodb", label: "Amazon DynamoDB key-value store" },
                { value: "oracle", label: "Oracle Enterprise DB" },
                { value: "cassandra", label: "Apache Cassandra distributed DB" },
                { value: "elasticsearch", label: "Elasticsearch indexing cluster" }
              ]
            },
            {
              qid: "microservices_count",
              label: "Approximate count of active backend microservices in scope",
              type: "dropdown",
              required: true,
              options: [
                { value: "monolith", label: "Monolithic architecture (1 unified service)" },
                { value: "small_mesh", label: "Small microservices mesh (2 to 5 isolated services)" },
                { value: "medium_mesh", label: "Medium microservices mesh (6 to 15 isolated services)" },
                { value: "large_mesh", label: "Large enterprise mesh (16+ isolated services)" }
              ]
            }
          ]
        },
        {
          id: "sec_web_auth",
          title: "3. Identity, Credentials & Authorization Scope",
          description: "Detail authentication strategies, user roles, credentials, and MFA bypass rules.",
          questions: [
            {
              qid: "auth_mechanisms",
              label: "User identity & authentication mechanisms",
              type: "multi_choice",
              required: true,
              options: [
                { value: "federated_sso", label: "Federated SSO / SAML 2.0 (Okta, Entra ID, Ping)" },
                { value: "oauth_oidc", label: "OAuth 2.0 / OpenID Connect (OIDC)" },
                { value: "jwt_bearer", label: "Standard JSON Web Tokens (JWT) / Bearer Auth" },
                { value: "session_cookies", label: "HttpOnly session-based cookies" },
                { value: "mfa_active", label: "Multi-Factor Authentication (MFA/2FA) active" },
                { value: "passwordless_webauthn", label: "Passwordless / WebAuthn standard" }
              ]
            },
            {
              qid: "role_personas",
              label: "Number of distinct user roles / privilege levels to test",
              type: "single_choice",
              required: true,
              options: [
                { value: "one_role", label: "1 role (Basic user only)" },
                { value: "two_roles", label: "2 roles (Administrator and Basic user)" },
                { value: "multiple_roles", label: "3 to 5 roles (Multiple customer & manager personas)" },
                { value: "complex_roles", label: "6+ roles (Complex multi-tenant hierarchical model)" }
              ]
            },
            {
              qid: "provisioning_method",
              label: "Testing credential provisioning methodology",
              type: "dropdown",
              required: true,
              options: [
                { value: "pre_created", label: "Pre-created test accounts (2 accounts per role)" },
                { value: "self_registration", label: "Custom self-registration flow allowed" },
                { value: "api_keys", label: "API auth tokens / API keys provided directly" },
                { value: "dev_headers", label: "Auth-bypass development headers (e.g. X-Test-User)" }
              ]
            },
            {
              qid: "mfa_bypass",
              label: "How will the scoping team bypass Multi-Factor Authentication (MFA)?",
              type: "single_choice",
              required: true,
              options: [
                { value: "mfa_recovery_codes", label: "Hardcoded MFA recovery/bypass codes provided" },
                { value: "mfa_totp_seed", label: "Standard seed / TOTP secret shared for Google Authenticator" },
                { value: "mfa_disabled", label: "MFA completely disabled for testing accounts" },
                { value: "mfa_staging_none", label: "Dedicated staging environment where MFA is not active" }
              ]
            }
          ]
        },
        {
          id: "sec_web_access",
          title: "4. Network Access & Safelisting",
          description: "Detail connectivity requirements, perimeter controls, and firewall safelists.",
          questions: [
            {
              qid: "ip_whitelisting",
              label: "Is source IP whitelisting possible on the target environments?",
              type: "yes_no",
              required: true
            },
            {
              qid: "network_access_type",
              label: "How should testers connect to the application host?",
              type: "dropdown",
              required: true,
              options: [
                { value: "public_internet", label: "Public internet (No VPN or IP restriction)" },
                { value: "ip_restricted", label: "Public internet with source IP safelisting" },
                { value: "vpn_required", label: "Private network access via Corporate Client VPN" }
              ]
            },
            {
              qid: "waf_action",
              label: "Action required on Web Application Firewall (WAF) during test",
              type: "single_choice",
              required: true,
              options: [
                { value: "disable_completely", label: "Disable WAF completely for testing accounts" },
                { value: "whitelist_ips", label: "Whitelist testing IP addresses on WAF rulesets" },
                { value: "keep_active", label: "Keep WAF active to test detection and blocking mechanisms" }
              ]
            },
            {
              qid: "api_rate_limits",
              label: "Are aggressive rate-limits configured on backend API endpoints?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_web_data",
          title: "5. Data Security & Compliance Controls",
          description: "Map target regulatory constraints and application data handling models.",
          questions: [
            {
              qid: "phi_pci_data",
              label: "Does the application process or store PHI, PII, or PCI-DSS cardholder data?",
              type: "yes_no",
              required: true
            },
            {
              qid: "data_encryption_transit",
              label: "Is HSTS (HTTP Strict Transport Security) enabled across all target domains?",
              type: "yes_no",
              required: true
            },
            {
              qid: "session_timeout",
              label: "Configured user session idle timeout duration",
              type: "dropdown",
              required: true,
              options: [
                { value: "timeout_short", label: "15 minutes or less" },
                { value: "timeout_medium", label: "15 to 60 minutes" },
                { value: "timeout_long", label: "Over 60 minutes / persistent sessions" }
              ]
            },
            {
              qid: "file_upload_allowed",
              label: "Are users permitted to upload custom files or attachments?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_web_ops",
          title: "6. Incident Response & Defensive Alerting",
          description: "Examine security operation logging and active scanning communication windows.",
          questions: [
            {
              qid: "active_monitoring",
              label: "Is there active monitoring/IDS configured on the staging environment?",
              type: "yes_no",
              required: true
            },
            {
              qid: "siem_active",
              label: "Are application security logs forwarded to a centralized SIEM?",
              type: "yes_no",
              required: true
            },
            {
              qid: "soc_contact",
              label: "Contact email of SOC or defensive team to receive test notifications",
              type: "email",
              required: false
            },
            {
              qid: "incident_response_plan",
              label: "Is there a documented incident response playbook for application-level breaches?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_web_targets",
          title: "7. Target Scope Configurations",
          description: "Define base URLs and API endpoints in scope.",
          questions: [
            {
              qid: "scoping_methodologies",
              label: "Web App Targets & API Gateways",
              type: "target_builder",
              required: true
            },
            {
              qid: "api_documentation_provided",
              label: "Is API documentation (Swagger/OpenAPI/Postman) available for scoping?",
              type: "single_choice",
              required: true,
              options: [
                { value: "openapi_json", label: "Yes, complete Swagger / OpenAPI JSON spec provided" },
                { value: "postman_collection", label: "Yes, fully populated Postman collection available" },
                { value: "no_documentation", label: "No, API structure must be reverse-engineered" }
              ]
            },
            {
              qid: "non_prod_environment",
              label: "Is the target environment a dedicated staging/sandbox host?",
              type: "yes_no",
              required: true
            },
            {
              qid: "restricted_functions",
              label: "Are there any high-risk operations (e.g., mail dispatch, payment execution) that must be avoided?",
              type: "long_text",
              required: false
            }
          ]
        }
      ]
    }
  },
  {
    name: "2. Cloud Security & Infrastructure Audit (AWS/Azure/GCP)",
    templateName: "Cloud Security & Infrastructure Audit (AWS/Azure/GCP)",
    schema: {
      sections: [
        {
          id: "sec_cloud_context",
          title: "1. Cloud Footprint & Business Context",
          description: "Identify cloud systems, business goals, environments, and access permissions.",
          questions: [
            {
              qid: "cloud_provider",
              label: "Primary cloud infrastructure provider in-scope",
              type: "dropdown",
              required: true,
              options: [
                { value: "aws", label: "Amazon Web Services (AWS)" },
                { value: "azure", label: "Microsoft Azure" },
                { value: "gcp", label: "Google Cloud Platform (GCP)" },
                { value: "hybrid_cloud", label: "Hybrid / Multi-Cloud Enterprise" }
              ]
            },
            {
              qid: "audit_goal",
              label: "Primary objective of this infrastructure audit",
              type: "single_choice",
              required: true,
              options: [
                { value: "best_practices", label: "Architecture Best Practices Alignment (e.g. AWS Well-Architected)" },
                { value: "compliance_readiness", label: "Compliance readiness (CIS Benchmarks, NIST-800, SOC2)" },
                { value: "privilege_abuse", label: "Active security posture & privilege abuse testing (Gray-box IAM attack)" },
                { value: "breach_review", label: "Post-incident breach review" }
              ]
            },
            {
              qid: "environment_type",
              label: "Primary environment type to be audited",
              type: "single_choice",
              required: true,
              options: [
                { value: "staging", label: "Staging / Sandbox environment (Safe for testing)" },
                { value: "production", label: "Complete production setup (Active monitoring)" },
                { value: "mirrored", label: "Production-mirrored secondary staging account" }
              ]
            },
            {
              qid: "read_only_access",
              label: "Will read-only console/CLI access be granted to the auditors?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_cloud_network",
          title: "2. Architecture & Virtual Networking",
          description: "Detail cloud network structures, compute platforms, IaC templates, and database services.",
          questions: [
            {
              qid: "network_isolation",
              label: "Virtual network routing and isolation setups",
              type: "multi_choice",
              required: true,
              options: [
                { value: "segmented_vpcs", label: "Segmented VPCs / VNets" },
                { value: "vpc_peering", label: "VPC Peering Connections" },
                { value: "transit_gateway", label: "AWS Transit Gateway / Azure Virtual WAN" },
                { value: "vpn_direct_connect", label: "VPN or Direct Connect corporate backbones" },
                { value: "private_link", label: "Private Link endpoints / VPC endpoints active" },
                { value: "public_internet", label: "Public internet-facing workloads only" }
              ]
            },
            {
              qid: "compute_orchestration",
              label: "Container orchestration & compute platforms in use",
              type: "multi_choice",
              required: true,
              options: [
                { value: "managed_k8s", label: "Managed Kubernetes (EKS, AKS, GKE)" },
                { value: "self_managed_k8s", label: "Self-managed Kubernetes on virtual hosts" },
                { value: "serverless_containers", label: "Serverless containers (ECS, Fargate, Cloud Run)" },
                { value: "legacy_vms", label: "Legacy Virtual Machines (EC2, Azure VM, Compute Engine)" },
                { value: "serverless_functions", label: "Serverless functions (Lambda, Azure Functions)" }
              ]
            },
            {
              qid: "iac_usage",
              label: "Infrastructure-as-Code (IaC) templates in scope for static analysis",
              type: "multi_choice",
              required: true,
              options: [
                { value: "terraform", label: "HashiCorp Terraform" },
                { value: "cloudformation", label: "AWS CloudFormation" },
                { value: "pulumi", label: "Pulumi" },
                { value: "ansible_chef", label: "Ansible / Chef / Puppet" },
                { value: "none_manual", label: "None - manual console clicks or CLI provisioning only" }
              ]
            },
            {
              qid: "database_paas",
              label: "Managed data stores and caches in scope",
              type: "multi_choice",
              required: true,
              options: [
                { value: "managed_sql", label: "Managed Relational DBs (RDS, Azure SQL)" },
                { value: "managed_nosql", label: "NoSQL DBs (DynamoDB, CosmosDB)" },
                { value: "in_memory_cache", label: "In-memory caches (ElastiCache, Redis Enterprise)" },
                { value: "object_storage", label: "Object storage buckets (S3, Azure Blob, GCS)" }
              ]
            }
          ]
        },
        {
          id: "sec_cloud_defenses",
          title: "3. Security Baseline & Alerting Controls",
          description: "Verify WAF, threat scanners, MFA policies, and KMS encryption key rules.",
          questions: [
            {
              qid: "perimeter_waf",
              label: "Are perimeter WAF (Web Application Firewall) systems active?",
              type: "yes_no",
              required: true
            },
            {
              qid: "threat_detection",
              label: "Cloud-native threat detection engines enabled",
              type: "multi_choice",
              required: true,
              options: [
                { value: "aws_guardduty", label: "AWS GuardDuty" },
                { value: "ms_defender", label: "Microsoft Defender for Cloud" },
                { value: "gcp_scc", label: "GCP Security Command Center" },
                { value: "third_party_siem", label: "Third-party SIEM integrations (Datadog, Splunk, Sentinel)" }
              ]
            },
            {
              qid: "iam_mfa",
              label: "Is multi-factor authentication (MFA) strictly enforced for all IAM identities?",
              type: "yes_no",
              required: true
            },
            {
              qid: "kms_encryption",
              label: "Are cloud data storage resources encrypted using custom KMS keys?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_cloud_access",
          title: "4. Access Provisioning & Security Auditing",
          description: "Detail standard identity federations, auditor credentials, and role assumptions.",
          questions: [
            {
              qid: "audit_credentials_method",
              label: "Credential delivery strategy for the cloud assessment",
              type: "dropdown",
              required: true,
              options: [
                { value: "cross_account_iam", label: "Cross-account IAM Role delegation with External ID" },
                { value: "dedicated_iam_user", label: "Dedicated IAM User with static programmatic access keys" },
                { value: "console_saml", label: "Console access federated via corporate SAML / Okta profile" },
                { value: "local_cli_credentials", label: "No direct access - configuration files shared via secure vault" }
              ]
            },
            {
              qid: "cross_account_role",
              label: "Is cross-account IAM role assumption configured for external security providers?",
              type: "yes_no",
              required: true
            },
            {
              qid: "bastion_host_active",
              label: "Are administrative sessions gated by dedicated Bastion/Jump Hosts?",
              type: "yes_no",
              required: true
            },
            {
              qid: "conditional_access",
              label: "Are Entra ID/IAM sessions governed by IP-restricted conditional access policies?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_cloud_compliance",
          title: "5. Compliance Policies & Asset Governance",
          description: "Specify framework benchmarks and corporate tagging guidelines.",
          questions: [
            {
              qid: "cloud_governance_framework",
              label: "Primary cloud compliance standard enforced on organization resources",
              type: "dropdown",
              required: true,
              options: [
                { value: "cis_benchmark", label: "CIS Foundations Benchmark" },
                { value: "nist_800_53", label: "NIST SP 800-53 Risk Management Framework" },
                { value: "fedramp_moderate", label: "FedRAMP Moderate Baseline Controls" },
                { value: "pci_dss_v4", label: "PCI-DSS v4.0 CDE Cloud Requirements" },
                { value: "no_formal_governance", label: "No formal framework - internal custom guidelines only" }
              ]
            },
            {
              qid: "asset_tagging_enforced",
              label: "Are cloud resource tags systematically enforced via organization policy rules?",
              type: "yes_no",
              required: true
            },
            {
              qid: "public_buckets_allowed",
              label: "Is public access permitted on object storage buckets for web assets?",
              type: "yes_no",
              required: true
            },
            {
              qid: "incident_response_drill",
              label: "Are cloud-focused security incident dry-runs conducted annually?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_cloud_logs",
          title: "6. Auditing Logs & SIEM Integration",
          description: "Analyze system trail capture, flow captures, and active threat alert loops.",
          questions: [
            {
              qid: "cloudtrail_log_retention",
              label: "Minimum retention timeframe for management plane audit records (e.g. AWS CloudTrail)",
              type: "dropdown",
              required: true,
              options: [
                { value: "retention_90_days", label: "90 days (Default provider threshold)" },
                { value: "retention_1_year", label: "1 year in dedicated cold object storage" },
                { value: "retention_compliance_7_years", label: "7 years (Regulatory/financial compliance mandate)" }
              ]
            },
            {
              qid: "centralized_siem",
              label: "Are cloud execution logs stream-ingested into a centralized SIEM instance?",
              type: "yes_no",
              required: true
            },
            {
              qid: "flow_logs_enabled",
              label: "Is VPC Flow Logging enabled on all active production subnets?",
              type: "yes_no",
              required: true
            },
            {
              qid: "guardrails_active",
              label: "Are organization-wide Service Control Policies (SCPs) active to prevent policy bypass?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_cloud_scoping",
          title: "7. Cloud Targets & Scope Registry",
          description: "Configure target accounts, VPC boundaries, and host segments.",
          questions: [
            {
              qid: "scoping_methodologies",
              label: "VPCs & Cloud Subscriptions",
              type: "target_builder",
              required: true
            },
            {
              qid: "critical_accounts_list",
              label: "Specific high-value cloud accounts/subscriptions that must undergo intensive audit",
              type: "long_text",
              required: false
            },
            {
              qid: "third_party_vendor_access",
              label: "Does this cloud environment host integrated third-party vendor platforms?",
              type: "yes_no",
              required: true
            },
            {
              qid: "out_of_scope_assets",
              label: "List of cloud resource namespaces or networks that are strictly excluded from testing",
              type: "long_text",
              required: false
            }
          ]
        }
      ]
    }
  },
  {
    name: "3. Mobile Application Security Pentest (iOS & Android)",
    templateName: "Mobile Application Security Pentest (iOS & Android)",
    schema: {
      sections: [
        {
          id: "sec_mobile_context",
          title: "1. Mobile Platform & Distribution Details",
          description: "Specify target OS, build sharing methods, offline capabilities, and localization scopes.",
          questions: [
            {
              qid: "mobile_platforms",
              label: "Target mobile platforms to audit",
              type: "multi_choice",
              required: true,
              options: [
                { value: "ios_native", label: "Apple iOS Application" },
                { value: "android_native", label: "Google Android Application" },
                { value: "hybrid_framework", label: "Cross-Platform Hybrid Framework (React Native, Flutter)" }
              ]
            },
            {
              qid: "distribution_method",
              label: "Testing build delivery mechanism",
              type: "dropdown",
              required: true,
              options: [
                { value: "public_store", label: "Public App Store / Google Play Store links" },
                { value: "testflight_firebase", label: "TestFlight / Firebase App Distribution" },
                { value: "mdm_enterprise", label: "MDM / Enterprise Profile installation" },
                { value: "source_compilation", label: "Direct compilation from source code" },
                { value: "binary_upload", label: "Binary file upload (.ipa / .apk)" }
              ]
            },
            {
              qid: "offline_capabilities",
              label: "Does the application have major offline operations/data storage?",
              type: "yes_no",
              required: true
            },
            {
              qid: "app_locales",
              label: "Number of target language locales and geographical regions",
              type: "single_choice",
              required: true,
              options: [
                { value: "single_locale", label: "Single country/locale version" },
                { value: "some_locales", label: "2 to 5 regional localized versions" },
                { value: "global_locales", label: "Multi-continental globally localized editions" }
              ]
            }
          ]
        },
        {
          id: "sec_mobile_tech",
          title: "2. Mobile Technical Frameworks",
          description: "Detail developer frameworks, API connections, obfuscation tools, and third-party SDKs.",
          questions: [
            {
              qid: "mobile_framework",
              label: "Development framework of the mobile binary",
              type: "single_choice",
              required: true,
              options: [
                { value: "native_languages", label: "Native Swift / Objective-C (iOS) & Kotlin / Java (Android)" },
                { value: "react_native", label: "React Native Hybrid Framework" },
                { value: "flutter_cross", label: "Flutter Cross-platform Framework" },
                { value: "xamarin_cordova", label: "Xamarin / Cordova Legacy Frameworks" }
              ]
            },
            {
              qid: "backend_api_protocols",
              label: "Backend API integration protocols",
              type: "multi_choice",
              required: true,
              options: [
                { value: "rest_http", label: "Standard REST API" },
                { value: "graphql_api", label: "GraphQL API Server" },
                { value: "grpc_protobuf", label: "gRPC over HTTP/2" },
                { value: "websocket_notification", label: "WebSocket notification endpoints" }
              ]
            },
            {
              qid: "obfuscation_applied",
              label: "Is code obfuscation or binary packing applied?",
              type: "dropdown",
              required: true,
              options: [
                { value: "heavy_obfuscation", label: "Yes — ProGuard/DexGuard (Android) or custom LLVM obfuscator (iOS)" },
                { value: "basic_minify", label: "Yes — basic frontend bundle minification only" },
                { value: "no_obfuscation", label: "No — standard release or debug build with symbol tables intact" }
              ]
            },
            {
              qid: "third_party_sdks",
              label: "Active third-party analytical or ad-delivery SDKs",
              type: "multi_choice",
              required: true,
              options: [
                { value: "firebase_analytics", label: "Firebase Analytics & Crashlytics" },
                { value: "amplitude", label: "Amplitude Product Analytics" },
                { value: "adjust_kochava", label: "Adjust / Kochava Attribution SDKs" },
                { value: "facebook_ad", label: "Facebook SDK / Graph API wrapper" }
              ]
            }
          ]
        },
        {
          id: "sec_mobile_device_security",
          title: "3. Device Security & Cryptography",
          description: "Evaluate biological locks, certificate setups, local datastores, and operating system protection checks.",
          questions: [
            {
              qid: "biometric_auth",
              label: "Does the application leverage device-native biometrics?",
              type: "yes_no",
              required: true
            },
            {
              qid: "ssl_pinning",
              label: "Does the application enforce SSL / Certificate Pinning?",
              type: "single_choice",
              required: true,
              options: [
                { value: "pinning_enforced", label: "Yes - fully enforced certificate verification" },
                { value: "pinning_trustmanager", label: "Yes - custom trust manager implemented" },
                { value: "pinning_none", label: "No - relies on operating system default CA store" }
              ]
            },
            {
              qid: "local_storage_crypto",
              label: "Is local SQLite / realm database fully encrypted?",
              type: "yes_no",
              required: true
            },
            {
              qid: "root_detection",
              label: "Does the application enforce jailbreak or root detection defenses?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_mobile_access",
          title: "4. Backend Credential & API Provisioning",
          description: "Outline credentials required for backend interactions during the mobile test.",
          questions: [
            {
              qid: "mobile_test_accounts",
              label: "Will dedicated pre-registered test accounts be provided?",
              type: "yes_no",
              required: true
            },
            {
              qid: "gateway_whitelist",
              label: "Will backend API endpoints bypass geo-blocking or IP firewalls for testers?",
              type: "yes_no",
              required: true
            },
            {
              qid: "auth_headers",
              label: "Are custom authentication header injections required for mobile traffic analysis?",
              type: "yes_no",
              required: true
            },
            {
              qid: "push_notification_test",
              label: "Is push notification delivery logic in scope for dynamic analysis?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_mobile_data",
          title: "5. Data Leakage & Platform Safety",
          description: "Analyze local memory exposure, background state caching, and OS clipboard integrations.",
          questions: [
            {
              qid: "clipboard_sharing",
              label: "Does the app copy sensitive parameters to the global OS clipboard?",
              type: "yes_no",
              required: true
            },
            {
              qid: "screenshot_blocking",
              label: "Is screenshot capture blocked inside sensitive panels?",
              type: "yes_no",
              required: true
            },
            {
              qid: "background_screen_mask",
              label: "Does the app display a blank mask when placed in the background?",
              type: "yes_no",
              required: true
            },
            {
              qid: "keychain_usage",
              label: "Does the app utilize the iOS Keychain / Android Keystore securely for session state?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_mobile_threat",
          title: "6. Reverse Engineering & Dynamic Attacks",
          description: "Detail structural controls to mitigate active binary analysis and hooking.",
          questions: [
            {
              qid: "frida_blocking",
              label: "Does the binary contain anti-debugging checks to block Frida hooking?",
              type: "yes_no",
              required: true
            },
            {
              qid: "emulation_prevention",
              label: "Does the application verify it is running on authentic hardware vs emulators?",
              type: "yes_no",
              required: true
            },
            {
              qid: "integrity_verification",
              label: "Does the application run custom checksum validations on its own resource assets?",
              type: "yes_no",
              required: true
            },
            {
              qid: "debugging_symbols",
              label: "Are production builds compiled with debug symbols left intact?",
              type: "dropdown",
              required: true,
              options: [
                { value: "stripped_symbols", label: "Symbols fully stripped (Production build optimal)" },
                { value: "partial_symbols", label: "Some symbols left intact (Mixed profile)" },
                { value: "unstripped_symbols", label: "Debug build loaded with active maps (Developer layout)" }
              ]
            }
          ]
        },
        {
          id: "sec_mobile_targets",
          title: "7. Target Mobile Binaries & Gateways",
          description: "Identify target application bundle identifiers and staging backend gateway coordinates.",
          questions: [
            {
              qid: "scoping_methodologies",
              label: "Mobile Binary & Gateway Scope",
              type: "target_builder",
              required: true
            },
            {
              qid: "backend_server_endpoints",
              label: "Primary production/staging backend API base URLs",
              type: "long_text",
              required: true
            },
            {
              qid: "custom_keys_required",
              label: "Are hardcoded cryptographic API keys or custom certificates required to establish connection?",
              type: "yes_no",
              required: true
            },
            {
              qid: "out_of_scope_features",
              label: "Specific screens or modules that testers must not load (e.g. dynamic face-recognition, actual payment submission)",
              type: "long_text",
              required: false
            }
          ]
        }
      ]
    }
  },
  {
    name: "4. AI & LLM Integration Assessment (Jailbreaks & Prompts)",
    templateName: "AI & LLM Integration Assessment (Jailbreaks & Prompts)",
    schema: {
      sections: [
        {
          id: "sec_ai_context",
          title: "1. AI Model & Inference Architecture",
          description: "Specify system backend providers, automation loops, context injection, and logging models.",
          questions: [
            {
              qid: "llm_provider",
              label: "Model inference strategy and backend engine",
              type: "single_choice",
              required: true,
              options: [
                { value: "saas_closed_api", label: "Closed-source SaaS APIs (OpenAI GPT, Anthropic Claude, Gemini)" },
                { value: "self_hosted_open", label: "Self-hosted open-weights models (Llama-3, Mistral, Gemma)" },
                { value: "custom_finetuned", label: "Fine-tuned in-house weights on dedicated cluster" }
              ]
            },
            {
              qid: "agentic_capabilities",
              label: "Does the AI system perform autonomous tool execution or multi-agent loops?",
              type: "yes_no",
              required: true
            },
            {
              qid: "prompt_template_injection",
              label: "Are system prompts dynamically populated with unvalidated user inputs?",
              type: "yes_no",
              required: true
            },
            {
              qid: "user_data_retention",
              label: "Is user-submitted conversational data persisted for model retraining?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ai_rag",
          title: "2. Retrieval-Augmented Generation & Vector Assets",
          description: "Map vector databases, token scales, and semantic indexing layouts.",
          questions: [
            {
              qid: "rag_vector_db",
              label: "Vector Database used for semantic retrieval",
              type: "dropdown",
              required: true,
              options: [
                { value: "pinecone_saas", label: "Pinecone SaaS Vector DB" },
                { value: "milvus_enterprise", label: "Milvus Enterprise / Open Source DB" },
                { value: "pgvector_postgres", label: "pgvector extension on PostgreSQL" },
                { value: "qdrant_weaviate", label: "Qdrant / Weaviate Vector Database" },
                { value: "none_pure_inference", label: "None - pure LLM inference without RAG" }
              ]
            },
            {
              qid: "vector_count",
              label: "Scale of index vectors / document chunks stored",
              type: "single_choice",
              required: true,
              options: [
                { value: "low_scale", label: "Under 100,000 document vectors" },
                { value: "mid_scale", label: "100,000 to 1,000,000 vectors" },
                { value: "high_scale", label: "Over 1,000,000 high-density vectors" }
              ]
            },
            {
              qid: "embeddings_model",
              label: "Embedding model utilized for semantic search encoding",
              type: "single_choice",
              required: true,
              options: [
                { value: "openai_embeddings", label: "OpenAI text-embedding-3 / legacy ada-002" },
                { value: "cohere_embeddings", label: "Cohere Embed V3 models" },
                { value: "huggingface_local", label: "HuggingFace local model (e.g. BGE-large-en)" },
                { value: "custom_vector_encoder", label: "Custom trained in-house vector encoder" }
              ]
            },
            {
              qid: "rag_source_data",
              label: "Source data types ingested into the Vector Database",
              type: "multi_choice",
              required: true,
              options: [
                { value: "public_docs", label: "Public manuals & product documentation" },
                { value: "internal_knowledge", label: "Internal employee wikis & proprietary papers" },
                { value: "pii_customer_files", label: "Customer contract records containing PII/financial data" },
                { value: "dynamic_web_scraping", label: "Dynamic web feeds scraped in real-time" }
              ]
            }
          ]
        },
        {
          id: "sec_ai_isolation",
          title: "3. Sandboxing & Inference Safeguards",
          description: "Verify sandbox tools, firewalls, and manual verification models.",
          questions: [
            {
              qid: "agent_sandbox",
              label: "AI tool execution sandbox type",
              type: "dropdown",
              required: true,
              options: [
                { value: "microvm_isolation", label: "MicroVM isolation (Firecracker, gVisor, WebAssembly)" },
                { value: "container_dock", label: "Containerized execution (Docker with restricted network/PID)" },
                { value: "native_process_host", label: "Native OS shell process (CRITICAL RISK - direct server access)" },
                { value: "no_sandbox_exec", label: "None — no autonomous shell or code execution features enabled" }
              ]
            },
            {
              qid: "guardrails_layer",
              label: "Active prompt injection and policy filtering guardrails",
              type: "multi_choice",
              required: true,
              options: [
                { value: "llama_guard", label: "Meta Llama Guard integration" },
                { value: "nemo_guardrails", label: "NVIDIA NeMo Guardrails policy layer" },
                { value: "regex_filters", label: "Custom regex, blocklists, and keyword filters" },
                { value: "length_limits", label: "Input / Output context length restriction limits" },
                { value: "no_guardrails", label: "None - raw model inference without guard rails" }
              ]
            },
            {
              qid: "human_in_the_loop",
              label: "Are safety-critical tool executions approved by a human-in-the-loop?",
              type: "yes_no",
              required: true
            },
            {
              qid: "output_filtering",
              label: "Is dynamic output filtering enabled to prevent LLM leaking sensitive keys or vulgar text?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ai_access",
          title: "4. Access Provisioning & Inference Control",
          description: "Outline endpoints, authentication credentials, and connection limits for testing AI applications.",
          questions: [
            {
              qid: "api_keys_provided",
              label: "Will dedicated test API keys for model gateways be provided?",
              type: "yes_no",
              required: true
            },
            {
              qid: "inference_endpoint_auth",
              label: "Authentication method governing client-side access to the model",
              type: "dropdown",
              required: true,
              options: [
                { value: "bearer_token", label: "Header Bearer Token authentication" },
                { value: "cookie_session", label: "HTTP Cookie session state" },
                { value: "mtls_certificate", label: "Mutual TLS Client Certificate" },
                { value: "no_auth_public", label: "Unauthenticated public web page exposure" }
              ]
            },
            {
              qid: "custom_system_prompts",
              label: "Will raw system prompt configurations be shared with the auditors?",
              type: "yes_no",
              required: true
            },
            {
              qid: "rate_limiting_ai",
              label: "Is dynamic rate limiting active on client-facing chat API routes?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ai_data_compliance",
          title: "5. Data Privacy & Compliance Controls",
          description: "Verify PII scrubbers, moderation layers, and historic vulnerability lists.",
          questions: [
            {
              qid: "pii_redaction",
              label: "Is there a pre-processing module that automatically sanitizes PII before sending it to the model?",
              type: "yes_no",
              required: true
            },
            {
              qid: "model_hallucination_handling",
              label: "Is an secondary evaluative LLM used to cross-verify the accuracy of primary model outputs?",
              type: "yes_no",
              required: true
            },
            {
              qid: "content_moderation_api",
              label: "Are queries run against safety moderation APIs (e.g. OpenAI Moderation Endpoint) prior to inference?",
              type: "yes_no",
              required: true
            },
            {
              qid: "jailbreak_history",
              label: "Has this system suffered successfully documented prompt-injection or jailbreak incidents in the past?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ai_monitoring",
          title: "6. Threat Monitoring & Alert Logs",
          description: "Detail conversational monitoring methods and auditing setups.",
          questions: [
            {
              qid: "llm_logging",
              label: "Conversational audit trail logging scale",
              type: "dropdown",
              required: true,
              options: [
                { value: "full_transcript", label: "Complete prompt and output text transcripts stored securely" },
                { value: "metadata_only", label: "Metadata only (Timestamp, tokens, user identifier)" },
                { value: "zero_logs", label: "No logging enabled (Privacy-first setup)" }
              ]
            },
            {
              qid: "anomaly_detection",
              label: "Are anomalous user inputs (e.g. repetitive probe questions) flagged by web alerts?",
              type: "yes_no",
              required: true
            },
            {
              qid: "prompt_abuse_alerts",
              label: "Do security systems trigger automated admin alerts when jailbreak scores spike?",
              type: "yes_no",
              required: true
            },
            {
              qid: "evaluation_harness",
              label: "Do you utilize automated red-teaming benchmark suites (e.g. Garak, Promptfoo) in development?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ai_scoping",
          title: "7. LLM Interfaces & Target Endpoints",
          description: "Define Chat UI targets, orchestration gateways, and vector API registries.",
          questions: [
            {
              qid: "scoping_methodologies",
              label: "Agent Gateways & LLM Interfaces",
              type: "target_builder",
              required: true
            },
            {
              qid: "prompt_templates_list",
              label: "List of system prompts or task descriptions in-scope",
              type: "long_text",
              required: false
            },
            {
              qid: "agent_tool_descriptions",
              label: "Brief summary of automated tools/APIs that the LLM is permitted to invoke autonomously",
              type: "long_text",
              required: false
            },
            {
              qid: "out_of_scope_llms",
              label: "Specific target models or test environments that are strictly excluded from testing",
              type: "long_text",
              required: false
            }
          ]
        }
      ]
    }
  },
  {
    name: "5. Secure Code & Software Supply Chain Review",
    templateName: "Secure Code & Software Supply Chain Review",
    schema: {
      sections: [
        {
          id: "sec_code_context",
          title: "1. Version Control & Repository Architecture",
          description: "Outline hosting suites, monorepo structures, policy rules, and webhook configurations.",
          questions: [
            {
              qid: "vcs_platform",
              label: "Primary Version Control System (VCS) platform",
              type: "dropdown",
              required: true,
              options: [
                { value: "github_enterprise", label: "GitHub Enterprise SaaS / Server" },
                { value: "gitlab_ultimate", label: "GitLab Ultimate / Self-Hosted" },
                { value: "bitbucket_center", label: "Bitbucket Cloud / Data Center" },
                { value: "aws_codecommit", label: "AWS CodeCommit / Azure Repos" }
              ]
            },
            {
              qid: "monorepo_vs_poly",
              label: "Repository organization pattern",
              type: "single_choice",
              required: true,
              options: [
                { value: "monorepo", label: "Monolithic repository (Single codebase containing multiple sub-projects)" },
                { value: "polyrepo", label: "Polyrepo pattern (Decentralized micro-repositories per service)" }
              ]
            },
            {
              qid: "branch_protection",
              label: "Are branch protection rules strictly enforced on production branches?",
              type: "yes_no",
              required: true
            },
            {
              qid: "third_party_webhooks",
              label: "Are third-party webhook integrations allowed to trigger pipelines?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_code_sast",
          title: "2. Languages & Static Application Security Testing (SAST)",
          description: "Detail programming dialects, scanner suites, and package dependencies.",
          questions: [
            {
              qid: "source_languages",
              label: "Select in-scope development languages",
              type: "multi_choice",
              required: true,
              options: [
                { value: "typescript_js", label: "TypeScript / JavaScript (Node.js)" },
                { value: "python_fastapi", label: "Python (FastAPI / Django / Flask)" },
                { value: "go_golang", label: "Go (Golang)" },
                { value: "rust_systems", label: "Rust (Systems / WebAssembly)" },
                { value: "java_spring", label: "Java / Spring Boot" },
                { value: "csharp_net", label: "C# / .NET Core" },
                { value: "ruby_rails", label: "Ruby on Rails" }
              ]
            },
            {
              qid: "sast_tooling",
              label: "Static analysis scanners currently configured in the pipelines",
              type: "multi_choice",
              required: true,
              options: [
                { value: "sonarqube", label: "SonarQube / SonarCloud" },
                { value: "semgrep", label: "Semgrep (Open Source / Pro edition)" },
                { value: "github_codeql", label: "GitHub Advanced Security (CodeQL)" },
                { value: "snyk_code", label: "Snyk Code static analyzer" },
                { value: "no_sast", label: "None - no automated static application security testing active" }
              ]
            },
            {
              qid: "dependency_scanning",
              label: "Vulnerability scanning engine for third-party packages",
              type: "dropdown",
              required: true,
              options: [
                { value: "snyk_oss", label: "Snyk Open Source dependency analyzer" },
                { value: "github_dependabot", label: "GitHub Dependabot Alerts & Upgrades" },
                { value: "owasp_depcheck", label: "OWASP Dependency-Check plugin" },
                { value: "mend_whitesource", label: "Mend.io (formerly WhiteSource) Enterprise" },
                { value: "no_dep_scans", label: "None - no third-party package dependency scanners active" }
              ]
            },
            {
              qid: "code_ownership",
              label: "Is code review enforced by CODEOWNERS file declarations?",
              type: "dropdown",
              required: true,
              options: [
                { value: "strict_ownership", label: "Yes - CODEOWNERS approvals strictly enforced on all PRs" },
                { value: "loose_ownership", label: "Yes - CODEOWNERS specified but bypass is permitted" },
                { value: "no_codeowners", label: "No formal ownership file configured in repository" }
              ]
            }
          ]
        },
        {
          id: "sec_code_infrastructure",
          title: "3. Secrets Management & IaC Verification",
          description: "Examine safe vault integration, config analyzers, and Dockerfile checkers.",
          questions: [
            {
              qid: "secrets_vault",
              label: "Build-time and runtime secrets storage vault",
              type: "dropdown",
              required: true,
              options: [
                { value: "hashicorp_vault", label: "HashiCorp Vault" },
                { value: "aws_secrets_manager", label: "AWS Secrets Manager / Systems Manager Parameter Store" },
                { value: "azure_key_vault", label: "Azure Key Vault / GCP Secret Manager" },
                { value: "github_encrypted_secrets", label: "GitHub Encrypted Secrets / GitLab Variables" },
                { value: "plain_env", label: "Plaintext environment files in container registry / host settings" }
              ]
            },
            {
              qid: "iac_scanners",
              label: "Are IaC configurations scanned for security misconfigurations?",
              type: "single_choice",
              required: true,
              options: [
                { value: "checkov_tfsec", label: "Yes — using Checkov, tfsec, or KICS in CI pipelines" },
                { value: "custom_platform_scans", label: "Yes — using managed cloud config scanners" },
                { value: "no_iac_scans", label: "No — infrastructure configuration templates are not automatically scanned" }
              ]
            },
            {
              qid: "container_base_scans",
              label: "Do you perform base image scanning on custom Dockerfiles?",
              type: "yes_no",
              required: true
            },
            {
              qid: "secret_leak_scanner",
              label: "Automated scan utility active to prevent committing plain secret variables",
              type: "dropdown",
              required: true,
              options: [
                { value: "gitleaks", label: "Gitleaks running as pre-commit hook or CI step" },
                { value: "trufflehog", label: "TruffleHog credential scanner in active pipeline" },
                { value: "github_secret_scan", label: "GitHub native Secret Scanning active on repository" },
                { value: "no_secret_leak_scan", label: "No automated credential scan configuration in place" }
              ]
            }
          ]
        },
        {
          id: "sec_code_delivery",
          title: "4. Code Access & Delivery Credentials",
          description: "Detail repository sharing mechanisms and access scopes for auditors.",
          questions: [
            {
              qid: "repo_access_method",
              label: "VCS repository sharing mechanism for testers",
              type: "dropdown",
              required: true,
              options: [
                { value: "read_only_invite", label: "Direct read-only user invitation on VCS platform" },
                { value: "saml_sso_team", label: "SAML SSO team enrollment on company tenant" },
                { value: "offline_zip_vault", label: "Offline code export shared via secure encrypted vault" },
                { value: "pat_provided", label: "Programmatic Personal Access Token (PAT) shared directly" }
              ]
            },
            {
              qid: "ssh_key_provisioning",
              label: "Will auditors be permitted to register custom SSH deployment keys?",
              type: "yes_no",
              required: true
            },
            {
              qid: "custom_pipelines_in_scope",
              label: "Are CI/CD pipeline script files (.github/workflows, .gitlab-ci.yml) in-scope for security assessment?",
              type: "yes_no",
              required: true
            },
            {
              qid: "developer_signed_commits",
              label: "Are GPG/SSH cryptographic signatures strictly required for push events?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_supply_chain",
          title: "5. Software Supply Chain Defenses",
          description: "Verify dependencies, package locking, and software bill of materials.",
          questions: [
            {
              qid: "sbom_generated",
              label: "Is a Software Bill of Materials (SBOM) systematically generated for each production build?",
              type: "yes_no",
              required: true
            },
            {
              qid: "package_pinning",
              label: "Are absolute versions strictly pinned (e.g. package-lock.json, poetry.lock) across all dependencies?",
              type: "yes_no",
              required: true
            },
            {
              qid: "private_dependency_conflicts",
              label: "Are there measures implemented to defend against Dependency Confusion attacks?",
              type: "yes_no",
              required: true
            },
            {
              qid: "vendor_risk_reviews",
              label: "Is there a structured security assessment protocol for open-source library adoption?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_code_runners",
          title: "6. Pipeline Auditing & Runner Monitoring",
          description: "Analyze runner privilege, runner lifecycle settings, and log retentions.",
          questions: [
            {
              qid: "pipeline_logs_retention",
              label: "CI/CD execution log history retention duration",
              type: "dropdown",
              required: true,
              options: [
                { value: "retention_30_days", label: "30 days or less (Standard basic scale)" },
                { value: "retention_90_days", label: "90 days pipeline history" },
                { value: "retention_long_siem", label: "1 year or more / forwarded directly to auditing systems" }
              ]
            },
            {
              qid: "runner_hardening",
              label: "Are self-hosted runners isolated at the operating system virtualization layer?",
              type: "yes_no",
              required: true
            },
            {
              qid: "privileged_containers",
              label: "Do runner pipelines require Docker-in-Docker (DinD) elevated permissions?",
              type: "yes_no",
              required: true
            },
            {
              qid: "ephemeral_runners",
              label: "Are build runners fully destroyed and recreated after every single execution?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_code_scoping",
          title: "7. Code Repository Target Scope",
          description: "Specify git repository URLs and branches in-scope.",
          questions: [
            {
              qid: "scoping_methodologies",
              label: "Repositories In-Scope",
              type: "target_builder",
              required: true
            },
            {
              qid: "target_branches",
              label: "Primary branch configurations targeted for source code audit",
              type: "long_text",
              required: true
            },
            {
              qid: "sensitive_files_filter",
              label: "Should high-value files (e.g. database schema migrations, auth controllers) undergo isolated security focus?",
              type: "yes_no",
              required: true
            },
            {
              qid: "excluded_third_party_modules",
              label: "Specify folder paths or sub-packages to bypass (e.g. node_modules, vendor, external/legacy-libs)",
              type: "long_text",
              required: false
            }
          ]
        }
      ]
    }
  },
  {
    name: "6. Active Directory & Internal Enterprise Network Pentest",
    templateName: "Active Directory & Internal Enterprise Network Pentest",
    schema: {
      sections: [
        {
          id: "sec_ad_context",
          title: "1. Active Directory Topology",
          description: "Register tree topologies, controller bounds, and Azure AD connect features.",
          questions: [
            {
              qid: "ad_forests",
              label: "Active Directory forest structures in scope",
              type: "dropdown",
              required: true,
              options: [
                { value: "single_forest", label: "Single isolated forest with simple domain structure" },
                { value: "multi_bidirectional", label: "Multi-forest with bidirectional trusts" },
                { value: "multi_unidirectional", label: "Multi-forest with unidirectional trusts (resource domains)" },
                { value: "hybrid_entra", label: "Hybrid Entra ID (Azure AD) with local domain sync" }
              ]
            },
            {
              qid: "dc_count",
              label: "Total active Domain Controllers (DCs) in scope",
              type: "single_choice",
              required: true,
              options: [
                { value: "few_dcs", label: "1 to 2 Domain Controllers (Small domain)" },
                { value: "medium_dcs", label: "3 to 5 Domain Controllers (Regional/Segmented)" },
                { value: "many_dcs", label: "More than 5 Domain Controllers (Global enterprise)" }
              ]
            },
            {
              qid: "azure_ad_sync",
              label: "Is Active Directory synchronized with Entra ID using AD Connect?",
              type: "yes_no",
              required: true
            },
            {
              qid: "trust_relationships",
              label: "Underlying trust relationship routing standard",
              type: "dropdown",
              required: true,
              options: [
                { value: "transitive_bidirectional", label: "Transitive, bidirectional internal forest trusts" },
                { value: "non_transitive_external", label: "Non-transitive external trust connectors" },
                { value: "no_external_trusts", label: "No trust relationships configured (Completely isolated domain)" }
              ]
            }
          ]
        },
        {
          id: "sec_ad_topology",
          title: "2. Network Topology & Endpoint Sizing",
          description: "Detail subnet scopes, hardware volumes, and directory service routing.",
          questions: [
            {
              qid: "network_endpoints",
              label: "Total internal enterprise hosts / workstations in scope",
              type: "single_choice",
              required: true,
              options: [
                { value: "small_endpoints", label: "Under 250 active endpoints" },
                { value: "mid_endpoints", label: "250 to 1,000 active endpoints" },
                { value: "large_endpoints", label: "1,001 to 5,000 active corporate endpoints" },
                { value: "enterprise_endpoints", label: "Over 5,000 corporate endpoints in multi-national layout" }
              ]
            },
            {
              qid: "segmented_vlans",
              label: "Are internal VLANs strictly segmented using network firewalls?",
              type: "yes_no",
              required: true
            },
            {
              qid: "internal_dns",
              label: "Is DNS managed via Active Directory Integrated DNS?",
              type: "yes_no",
              required: true
            },
            {
              qid: "dhcp_security",
              label: "Is DHCP Snooping or Dynamic ARP Inspection (DAI) enabled on switch stacks?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ad_access",
          title: "3. Testing Access Provisioning",
          description: "Detail VPN connections, domain credentials, and whitelisting overrides.",
          questions: [
            {
              qid: "access_mode",
              label: "Auditor network access delivery mechanism",
              type: "dropdown",
              required: true,
              options: [
                { value: "vpn_profile", label: "Virtual VPN Profile (WireGuard, OpenVPN, IPSec)" },
                { value: "testing_vm", label: "Pre-configured testing Virtual Machine on internal hypervisor (ESXi, Hyper-V)" },
                { value: "physical_dropbox", label: "Physical drop-box mailed to corporate branch office" },
                { value: "client_agent", label: "Zero-trust client agent installation (e.g. Teleport, Twingate)" }
              ]
            },
            {
              qid: "domain_credentials",
              label: "Will the scoping team receive initial low-privileged domain credentials?",
              type: "single_choice",
              required: true,
              options: [
                { value: "single_user", label: "Yes — standard non-admin domain user credentials" },
                { value: "dual_users", label: "Yes — two different domain accounts for escalation verification" },
                { value: "zero_knowledge", label: "No — zero-knowledge initial network access (Simulated breach from lobby)" }
              ]
            },
            {
              qid: "whitelisted_ips",
              label: "Will the testers' source IPs be bypassed on internal IPS/IDS systems?",
              type: "yes_no",
              required: true
            },
            {
              qid: "gpo_access",
              label: "Is read-only network access to Group Policy Objects (GPOs) allowed?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ad_protocols",
          title: "4. Legacy Protocols & Authentication Vulnerabilities",
          description: "Examine status of LLMNR, SMB signing, Kerberos settings, and NTLM controls.",
          questions: [
            {
              qid: "llmnr_nbt_ns",
              label: "Are legacy name resolution protocols (LLMNR / NetBIOS) active on endpoints?",
              type: "yes_no",
              required: true
            },
            {
              qid: "smb_signing",
              label: "Is SMB Packet Signing strictly enforced across all hosts?",
              type: "single_choice",
              required: true,
              options: [
                { value: "enforced_all", label: "Enforced everywhere (High protection)" },
                { value: "enforced_servers", label: "Enforced on domain controllers / critical servers only" },
                { value: "not_enforced", label: "Not enforced anywhere (Relaying risk present)" }
              ]
            },
            {
              qid: "kerberos_preauth",
              label: "Is Kerberos Pre-Authentication disabled on any domain accounts?",
              type: "yes_no",
              required: true
            },
            {
              qid: "ntlm_v1_active",
              label: "Is the highly insecure NTLM v1 protocol still permitted in the domain?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ad_defenses",
          title: "5. Server & Workstation Defenses",
          description: "Detail local host security layers, disk encryptions, and administrator credential protection rules.",
          questions: [
            {
              qid: "edr_platform",
              label: "Primary endpoint security (EDR/AV) suite active on network systems",
              type: "dropdown",
              required: true,
              options: [
                { value: "crowdstrike", label: "CrowdStrike Falcon Insight" },
                { value: "sentinel_one", label: "SentinelOne Active EDR" },
                { value: "ms_defender_endpoint", label: "Microsoft Defender for Endpoint (MDE)" },
                { value: "basic_av_only", label: "Standard signature-based AV only (No active EDR telemetry)" },
                { value: "no_endpoint_protection", label: "None - no endpoint security software installed" }
              ]
            },
            {
              qid: "laps_enabled",
              label: "Is Windows LAPS (Local Administrator Password Solution) configured on domain hosts?",
              type: "yes_no",
              required: true
            },
            {
              qid: "bitlocker_active",
              label: "Is full-disk encryption (e.g. BitLocker) enforced on corporate workstations?",
              type: "yes_no",
              required: true
            },
            {
              qid: "wdigest_caching",
              label: "Is WDigest plaintext credential caching disabled in the registry settings?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ad_audit",
          title: "6. Enterprise Monitoring & Log Forwarding",
          description: "Verify security event collectors, SIEM routing, and flow analyzer states.",
          questions: [
            {
              qid: "siem_forwarding",
              label: "Are local domain controller event logs forwarded to a centralized SIEM system?",
              type: "yes_no",
              required: true
            },
            {
              qid: "windows_event_forwarding",
              label: "Is Windows Event Forwarding (WEF) configured to aggregate workstation alert streams?",
              type: "yes_no",
              required: true
            },
            {
              qid: "privilege_account_alerts",
              label: "Do system monitors trigger immediate alerts when changes occur in Domain Admin groups?",
              type: "yes_no",
              required: true
            },
            {
              qid: "network_flow_analysis",
              label: "Is NetFlow or network traffic analysis (NTA) active to detect lateral movement?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ad_scoping",
          title: "7. Enterprise CIDR Segments & Active Subnets",
          description: "Map target internal IP ranges and Active Directory domain names.",
          questions: [
            {
              qid: "scoping_methodologies",
              label: "Internal CIDRs & Domain Controllers",
              type: "target_builder",
              required: true
            },
            {
              qid: "target_domain_names",
              label: "Active Directory Domain Name (FQDN) (e.g., corp.company.local)",
              type: "long_text",
              required: true
            },
            {
              qid: "sensitive_servers_exclude",
              label: "Specify list of mission-critical systems that must be completely avoided during scan tests (e.g., legacy industrial controllers)",
              type: "long_text",
              required: false
            },
            {
              qid: "critical_hosts_ips",
              label: "Hostnames and IP addresses of Domain Controllers in scope",
              type: "long_text",
              required: false
            }
          ]
        }
      ]
    }
  },
  {
    name: "7. SaaS & M365 Governance Audit",
    templateName: "SaaS & M365 Governance Audit",
    schema: {
      sections: [
        {
          id: "sec_saas_context",
          title: "1. SaaS Platform Scope Dimensions",
          description: "Map targeted suites, headcount licenses, and classification levels.",
          questions: [
            {
              qid: "saas_platforms",
              label: "Target SaaS suites to audit",
              type: "multi_choice",
              required: true,
              options: [
                { value: "m365_tenant", label: "Microsoft 365 Tenant (Exchange, SharePoint, Teams)" },
                { value: "google_workspace", label: "Google Workspace Organization" },
                { value: "salesforce_crm", label: "Salesforce Enterprise CRM Instance" },
                { value: "slack_enterprise", label: "Slack Enterprise Grid Suite" },
                { value: "github_org", label: "GitHub Enterprise Organization" }
              ]
            },
            {
              qid: "active_seat_licenses",
              label: "Total seat count / licensed user accounts",
              type: "single_choice",
              required: true,
              options: [
                { value: "low_seats", label: "Under 200 users" },
                { value: "mid_seats", label: "200 to 1,000 user licenses" },
                { value: "high_seats", label: "1,001 to 5,000 user licenses" },
                { value: "massive_seats", label: "Over 5,000 active user accounts" }
              ]
            },
            {
              qid: "data_sensitivity",
              label: "Primary sensitivity classification of data hosted in the target SaaS",
              type: "dropdown",
              required: true,
              options: [
                { value: "financial_pii", label: "Highly Sensitive (Financial ledger, PHI, customer credentials)" },
                { value: "operational_data", label: "Moderately Sensitive (Internal operational business data, roadmaps)" },
                { value: "public_info", label: "Publicly Releasable information only" }
              ]
            },
            {
              qid: "connected_third_party_apps",
              label: "Are users permitted to grant OAuth app permissions to third-party integrations?",
              type: "dropdown",
              required: true,
              options: [
                { value: "allow_all_apps", label: "Yes - users can self-authorize any third-party app" },
                { value: "admin_approval_gated", label: "Yes - but requests are gated by administrative approval workflows" },
                { value: "block_all_external", label: "No - all third-party marketplace integrations are strictly blocked" }
              ]
            }
          ]
        },
        {
          id: "sec_saas_auth",
          title: "2. Identity, MFA & Access Management",
          description: "Verify IdPs, MFA settings, guest link rules, and admin populations.",
          questions: [
            {
              qid: "idp_platform",
              label: "Primary Identity Provider (IdP) federating SaaS logins",
              type: "dropdown",
              required: true,
              options: [
                { value: "entra_id", label: "Entra ID (formerly Azure Active Directory)" },
                { value: "okta_cloud", label: "Okta Identity Cloud" },
                { value: "ping_identity", label: "Ping Identity Suite" },
                { value: "google_idp", label: "Google Cloud Identity" },
                { value: "native_passwords", label: "Native SaaS credentials (No centralized Single Sign-On)" }
              ]
            },
            {
              qid: "mfa_coverage",
              label: "Multi-Factor Authentication (MFA) enforcement status",
              type: "single_choice",
              required: true,
              options: [
                { value: "all_mfa", label: "Strictly enforced for all accounts (100% user base)" },
                { value: "admins_mfa", label: "Enforced for global/billing administrators only" },
                { value: "optional_mfa", label: "Enabled on the tenant but optional for general users" },
                { value: "disabled_mfa", label: "MFA completely disabled (High risk profile)" }
              ]
            },
            {
              qid: "external_sharing_allowed",
              label: "Are guest users permitted to share file links with external domains?",
              type: "yes_no",
              required: true
            },
            {
              qid: "admin_count",
              label: "Approximate count of global administrators / super-admins",
              type: "single_choice",
              required: true,
              options: [
                { value: "few_admins", label: "1 to 3 admin accounts" },
                { value: "medium_admins", label: "4 to 8 admin accounts" },
                { value: "many_admins", label: "More than 8 global admin accounts" }
              ]
            }
          ]
        },
        {
          id: "sec_saas_monitoring",
          title: "3. Logging, Monitoring & DLP",
          description: "Detail retention timelines, broker settings, and data loss blockers.",
          questions: [
            {
              qid: "audit_logs",
              label: "Audit log retention period configured on the tenant",
              type: "single_choice",
              required: true,
              options: [
                { value: "short_logs", label: "30 days or less (Default cloud basic tiers)" },
                { value: "medium_logs", label: "90 days retention" },
                { value: "long_logs", label: "1 year or more / forwarded to external SIEM/data warehouse" }
              ]
            },
            {
              qid: "casb_in_use",
              label: "Is a Cloud Access Security Broker (CASB) active on the tenant?",
              type: "yes_no",
              required: true
            },
            {
              qid: "dlp_rules",
              label: "Are Data Loss Prevention (DLP) rules configured for file uploads/downloads?",
              type: "yes_no",
              required: true
            },
            {
              qid: "e_discovery_active",
              label: "Is eDiscovery or legal hold compliance systematically configured for internal mailboxes?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_saas_credentials",
          title: "4. Audit Credentials & Access Provisioning",
          description: "Detail setup parameters for auditing roles, API reader scopes, and conditional blocks.",
          questions: [
            {
              qid: "audit_account_provisioning",
              label: "How will the scoping team access the administration panel for analysis?",
              type: "dropdown",
              required: true,
              options: [
                { value: "read_only_global_admin", label: "Dedicated Global Reader / Read-Only Auditor role" },
                { value: "screen_sharing_interactive", label: "Interactive screen-share walkthrough with active tenant admins" },
                { value: "scripted_export_json", label: "No direct access - configuration parameters exported via PowerShell/API script JSONs" }
              ]
            },
            {
              qid: "api_read_only_access",
              label: "Will API credentials with read-only organization metadata access be granted?",
              type: "yes_no",
              required: true
            },
            {
              qid: "privileged_identity_management",
              label: "Is Privileged Identity Management (PIM) active for just-in-time administrative escalation?",
              type: "yes_no",
              required: true
            },
            {
              qid: "conditional_access_policies",
              label: "Are there conditional access controls in place that must be updated to permit auditor logins?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_saas_email",
          title: "5. Email & Collaboration Defenses",
          description: "Check DNS records, spam filter actions, and external teams collaboration configurations.",
          questions: [
            {
              qid: "dkim_spf_dmarc",
              label: "Are SPF, DKIM, and DMARC fully configured and actively enforced for corporate email domains?",
              type: "yes_no",
              required: true
            },
            {
              qid: "phishing_protection",
              label: "Primary automated mail gateway/anti-phishing filter active",
              type: "dropdown",
              required: true,
              options: [
                { value: "m365_defender_mail", label: "Microsoft Defender for Office 365" },
                { value: "google_workspace_native", label: "Google Workspace native spam/phishing filters" },
                { value: "proofpoint_mimecast", label: "Third-party gateway (Proofpoint, Mimecast, Abnormal Security)" },
                { value: "no_advanced_filter", label: "None - basic standard POP/IMAP default spam checks only" }
              ]
            },
            {
              qid: "teams_sharepoint_sharing",
              label: "SharePoint / Teams external collaboration baseline settings",
              type: "single_choice",
              required: true,
              options: [
                { value: "sharing_anybody", label: "Anyone - anonymous links permitted (Insecure profile)" },
                { value: "sharing_authenticated_guests", label: "Existing/Authenticated external guests only" },
                { value: "sharing_domain_restricted", label: "Whitelisted target domains only (Gated layout)" },
                { value: "sharing_blocked_completely", label: "Blocked - no external sharing allowed under any circumstances" }
              ]
            },
            {
              qid: "mailbox_forwarding_rules",
              label: "Are automatic external forwarding rules systematically blocked at the Exchange/mail server level?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_saas_device",
          title: "6. Endpoint Compliance & Device Policies",
          description: "Examine mobile management setups, enforcement checkpoints, and custom configurations.",
          questions: [
            {
              qid: "mdm_intune",
              label: "Is an active Mobile Device Management (MDM / Microsoft Intune) system deployed?",
              type: "yes_no",
              required: true
            },
            {
              qid: "device_compliance_check",
              label: "Are client systems checked for OS patches prior to mounting cloud file systems?",
              type: "yes_no",
              required: true
            },
            {
              qid: "unmanaged_device_block",
              label: "Are unmanaged or personal devices completely blocked from accessing core corporate tenants?",
              type: "yes_no",
              required: true
            },
            {
              qid: "personal_device_allowed",
              label: "Is BYOD (Bring Your Own Device) permitted under basic virtualization wrappers?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_saas_scoping",
          title: "7. Tenant Target Profiles",
          description: "List target domain names and directory identifiers.",
          questions: [
            {
              qid: "scoping_methodologies",
              label: "SaaS Tenant IDs & Domains",
              type: "target_builder",
              required: true
            },
            {
              qid: "target_tenants_list",
              label: "Specific target domains and Microsoft 365 / Google Workspace tenant IDs in-scope",
              type: "long_text",
              required: true
            },
            {
              qid: "excluded_saas_features",
              label: "List of highly sensitive SaaS divisions to bypass (e.g. legal legal-hold storage, live customer billing files)",
              type: "long_text",
              required: false
            },
            {
              qid: "audit_timeframe_sla",
              label: "Preferred target window to run active configurations extraction",
              type: "single_choice",
              required: true,
              options: [
                { value: "business_hours", label: "Standard business hours (9 AM - 5 PM SLA)" },
                { value: "off_peak_hours", label: "Off-peak / Weekend extraction schedule" }
              ]
            }
          ]
        }
      ]
    }
  },
  {
    name: "8. External Network & Threat Exposure Assessment",
    templateName: "External Network & Threat Exposure Assessment",
    schema: {
      sections: [
        {
          id: "sec_ext_perimeter",
          title: "1. External IP Perimeter Dimensions",
          description: "Map target IP footprint sizes, hosting setups, and name server backends.",
          questions: [
            {
              qid: "external_ip_scale",
              label: "Scale of public-facing IPv4/IPv6 footprint",
              type: "single_choice",
              required: true,
              options: [
                { value: "small_ips", label: "1 to 15 active IP addresses" },
                { value: "mid_ips", label: "16 to 100 IP addresses" },
                { value: "large_ips", label: "101 to 500 IP addresses" },
                { value: "enterprise_ips", label: "Over 500 corporate IP addresses in scope" }
              ]
            },
            {
              qid: "ip_ownership",
              label: "Do you own the netblocks directly, or are they hosted in cloud platforms?",
              type: "single_choice",
              required: true,
              options: [
                { value: "cloud_dynamic", label: "Cloud provider netblocks (AWS/Azure/GCP dynamic public IPs)" },
                { value: "corporate_allocations", label: "Owned corporate subnets (ARIN/RIPE allocations directly)" },
                { value: "hybrid_footprint", label: "Mixed hybrid external footprint" }
              ]
            },
            {
              qid: "dns_servers",
              label: "Primary external DNS hosting provider",
              type: "dropdown",
              required: true,
              options: [
                { value: "cloudflare_dns", label: "Cloudflare DNS" },
                { value: "aws_route53", label: "AWS Route 53 Managed DNS" },
                { value: "registrar_default", label: "GoDaddy / registrar default DNS hosts" },
                { value: "self_hosted_dns", label: "Self-hosted DNS servers (Bind9, Windows Active Directory DNS)" }
              ]
            },
            {
              qid: "asn_count",
              label: "Total autonomous system numbers (ASNs) owned directly",
              type: "single_choice",
              required: true,
              options: [
                { value: "zero_asn", label: "Zero - we do not own dynamic BGP ASNs" },
                { value: "one_asn", label: "1 active ASN registration" },
                { value: "multiple_asn", label: "2 or more international ASN blocks" }
              ]
            }
          ]
        },
        {
          id: "sec_ext_defenses",
          title: "2. Perimeter Border Defenses & Gateways",
          description: "Examine VPN models, open port scopes, and intrusion blockages.",
          questions: [
            {
              qid: "remote_access_vpn",
              label: "Active remote access corporate VPN appliance",
              type: "dropdown",
              required: true,
              options: [
                { value: "palo_alto_gp", label: "Palo Alto Networks GlobalProtect" },
                { value: "cisco_asa_any", label: "Cisco ASA / Firepower AnyConnect" },
                { value: "fortinet_forti", label: "Fortinet FortiGate SSL VPN Gateway" },
                { value: "f5_bigip", label: "F5 BIG-IP Edge Client Gateway" },
                { value: "ztna_zero", label: "None - Zero Trust Network Access (ZTNA) web proxy configured" }
              ]
            },
            {
              qid: "public_ingress_ports",
              label: "Primary public ports exposed to the internet",
              type: "multi_choice",
              required: true,
              options: [
                { value: "web_ports", label: "HTTP / HTTPS (80, 443)" },
                { value: "shell_ports", label: "SSH / SFTP (22)" },
                { value: "rdp_ports", label: "Remote Desktop RDP (3389)" },
                { value: "db_endpoints", label: "Database endpoints (3306, 5432, 1433)" },
                { value: "vpn_ports", label: "VPN encapsulation channels (UDP 500, 4500)" }
              ]
            },
            {
              qid: "ips_active",
              label: "Is an active Intrusion Prevention System (IPS) running on the edge?",
              type: "yes_no",
              required: true
            },
            {
              qid: "router_hardening",
              label: "Are administrative consoles (e.g. router SSH/HTTP portals) completely disabled on public facing IPs?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ext_mitigations",
          title: "3. DDoS & Edge Protection Controls",
          description: "Evaluate CDN networks, limiters, and whitelisting methods.",
          questions: [
            {
              qid: "cdn_edge",
              label: "Edge CDN and DDoS protection service in place",
              type: "dropdown",
              required: true,
              options: [
                { value: "cloudflare_ent", label: "Cloudflare Enterprise CDN / Magic Transit" },
                { value: "akamai_web", label: "Akamai Web Application Protector" },
                { value: "fastly_sec", label: "Fastly Next-Gen WAF & CDN" },
                { value: "aws_shield", label: "AWS CloudFront with Shield Advanced" },
                { value: "none_direct", label: "None - traffic flows directly to hosting origins" }
              ]
            },
            {
              qid: "rate_limiting",
              label: "Are request rate-limiters active on critical web gateways?",
              type: "yes_no",
              required: true
            },
            {
              qid: "whitelisting_required",
              label: "Will the scoping team be whitelisted or blocked by default during testing?",
              type: "single_choice",
              required: true,
              options: [
                { value: "gray_box_bypass", label: "Bypassed / Whitelisted on WAF and IPS (Gray-box testing focus on logic)" },
                { value: "black_box_block", label: "Fully blocked - test our active defensive blocking controls (Black-box testing)" }
              ]
            },
            {
              qid: "origin_ip_masking",
              label: "Are origin server real IP addresses strictly hidden behind edge proxy arrays?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ext_scanning",
          title: "4. Testing IP Provisioning & Whitelist Parameters",
          description: "Outline network safelisting coordinates, load-balancers, and target cryptography thresholds.",
          questions: [
            {
              qid: "auditor_ip_safelist",
              label: "Will a list of target testing IPv4 ranges be whitelisted on edge IPS rulesets?",
              type: "yes_no",
              required: true
            },
            {
              qid: "load_balancer_rules",
              label: "Target internet load balancer layer",
              type: "dropdown",
              required: true,
              options: [
                { value: "aws_alb_nlb", label: "AWS Application/Network Load Balancer (ALB/NLB)" },
                { value: "f5_big_ip_hardware", label: "F5 BIG-IP hardware load balancers" },
                { value: "haproxy_nginx", label: "Software HAProxy / NGINX load balancer proxies" },
                { value: "no_load_balancer", label: "No load balancer - direct individual server mappings" }
              ]
            },
            {
              qid: "external_vulnerability_scanner",
              label: "Are automated external scanners (e.g. Nessus, Qualys) routinely executed on this scope?",
              type: "yes_no",
              required: true
            },
            {
              qid: "ssl_tls_standards",
              label: "Enforced baseline cryptographic standard for perimeter servers",
              type: "dropdown",
              required: true,
              options: [
                { value: "modern_tls13", label: "Modern (TLS 1.3 preferred, absolute TLS 1.2 minimum)" },
                { value: "intermediate_tls12", label: "Intermediate (TLS 1.2 active with select legacy ciphers for client reach)" },
                { value: "legacy_weak", label: "Legacy (TLS 1.1 / TLS 1.0 active for backward compatibility)" }
              ]
            }
          ]
        },
        {
          id: "sec_ext_email",
          title: "5. Domain & Email Security Defenses",
          description: "Verify core DNS protection structures, security hooks, and mailbox routing.",
          questions: [
            {
              qid: "dmarc_policy",
              label: "Active DMARC record policy configuration on public domains",
              type: "dropdown",
              required: true,
              options: [
                { value: "dmarc_reject", label: "Reject (Strictly block unauthorized spoofing attempts)" },
                { value: "dmarc_quarantine", label: "Quarantine (Spam folder containment)" },
                { value: "dmarc_none", label: "None (Monitoring mode only - spoofing allowed)" },
                { value: "no_dmarc", label: "No DMARC record published (Critical domain vulnerability)" }
              ]
            },
            {
              qid: "dnssec_enabled",
              label: "Is DNSSEC (DNS Security Extensions) enabled on the zone records?",
              type: "yes_no",
              required: true
            },
            {
              qid: "subdomain_takeover_check",
              label: "Is there active monitoring to flag unused CNAME records pointing to dead SaaS hosts?",
              type: "yes_no",
              required: true
            },
            {
              qid: "mx_records_hosted",
              label: "Host location for primary corporate MX mail servers",
              type: "dropdown",
              required: true,
              options: [
                { value: "m365_cloud", label: "Microsoft 365 Exchange Online" },
                { value: "google_workspace_mx", label: "Google Workspace Gmail MX servers" },
                { value: "on_premise_exchange", label: "On-Premise Exchange Server / local gateway" }
              ]
            }
          ]
        },
        {
          id: "sec_ext_threat",
          title: "6. Threat Intelligence & Brand Exposure",
          description: "Scan brand leaks, dark-web exposures, credential breaches, and endpoint key leaks.",
          questions: [
            {
              qid: "dark_web_monitoring",
              label: "Is active monitoring conducted for compromised corporate domains on the dark web?",
              type: "yes_no",
              required: true
            },
            {
              qid: "domain_typosquatting",
              label: "Are alerts generated for newly registered typosquatting/phishing domains?",
              type: "yes_no",
              required: true
            },
            {
              qid: "compromised_creds_alert",
              label: "Are corporate email credentials actively tracked in historic public database leaks?",
              type: "yes_no",
              required: true
            },
            {
              qid: "leaked_api_keys",
              label: "Do scanners monitor public GitHub repositories for accidentally committed internal keys?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ext_scoping",
          title: "7. External Scoping Targets",
          description: "Configure external CIDR blocks and top-level DNS domains.",
          questions: [
            {
              qid: "scoping_methodologies",
              label: "Perimeter CIDRs & Domains",
              type: "target_builder",
              required: true
            },
            {
              qid: "target_external_domains",
              label: "Top-Level Domains (TLDs) in scope (e.g. company.com, staging.company.com)",
              type: "long_text",
              required: true
            },
            {
              qid: "excluded_networks",
              label: "Explicit list of IP addresses or third-party hosted subnets to exclude from scanning",
              type: "long_text",
              required: false
            },
            {
              qid: "special_testing_window",
              label: "Specific timing constraints to execute active port-scans",
              type: "single_choice",
              required: true,
              options: [
                { value: "anytime_24_7", label: "No constraints (24/7 continuous testing window)" },
                { value: "night_weekend_only", label: "Off-business hours (6 PM to 6 AM and weekends only)" }
              ]
            }
          ]
        }
      ]
    }
  },
  {
    name: "9. DevSecOps Pipeline & CI/CD Security Audit",
    templateName: "DevSecOps Pipeline & CI/CD Security Audit",
    schema: {
      sections: [
        {
          id: "sec_pipeline_context",
          title: "1. Continuous Integration Platforms",
          description: "Identify server platforms, runner isolation models, and triggers.",
          questions: [
            {
              qid: "images_pipeline_ci_platforms",
              label: "Primary Continuous Integration (CI) platforms in use",
              type: "multi_choice",
              required: true,
              options: [
                { value: "github_actions", label: "GitHub Actions runner suites" },
                { value: "gitlab_ci", label: "GitLab CI/CD automated lines" },
                { value: "jenkins_server", label: "Jenkins automation server" },
                { value: "circle_ci", label: "CircleCI hosted platform" },
                { value: "azure_pipelines", label: "Azure Pipelines / AWS CodePipeline" }
              ]
            },
            {
              qid: "runner_isolation",
              label: "Pipeline execution runner architecture",
              type: "dropdown",
              required: true,
              options: [
                { value: "managed_saas_runners", label: "Managed SaaS virtual machines (GitHub-hosted, GitLab SaaS)" },
                { value: "self_hosted_vms", label: "Self-hosted Virtual Machines inside private network boundaries" },
                { value: "k8s_ephemeral_pods", label: "Ephemeral container pods spawned dynamically on Kubernetes nodes" }
              ]
            },
            {
              qid: "pipeline_triggers",
              label: "What triggers a standard CI pipeline execution?",
              type: "multi_choice",
              required: true,
              options: [
                { value: "pull_requests", label: "Pull request / branch merge proposal validations" },
                { value: "direct_commits", label: "Direct commits pushed to protected main/master branch" },
                { value: "scheduled_cron", label: "Scheduled hourly/daily cron automation tasks" },
                { value: "manual_api", label: "Manual dashboard trigger or external webhook invocation" }
              ]
            },
            {
              qid: "runner_environment",
              label: "Operating system tier running the build execution engines",
              type: "dropdown",
              required: true,
              options: [
                { value: "linux_ubuntu", label: "Linux Ubuntu / Debian server distributions" },
                { value: "windows_server", label: "Windows Server / PowerShell automation base" },
                { value: "macos_darwin", label: "macOS Xcode build hosts for iOS development" }
              ]
            }
          ]
        },
        {
          id: "sec_pipeline_containers",
          title: "2. Container Security & Package Registries",
          description: "Detail custom registries, image signatures, and vulnerability scans.",
          questions: [
            {
              qid: "package_registry",
              label: "Primary artifact / Docker container registry",
              type: "dropdown",
              required: true,
              options: [
                { value: "docker_hub_ent", label: "Docker Hub Enterprise Registry" },
                { value: "cloud_native_registry", label: "AWS ECR / Azure ACR / GCP Google Artifact Registry" },
                { value: "jfrog_artifactory", label: "JFrog Artifactory Repository manager" },
                { value: "github_packages", label: "GitHub Container Registry / Packages" },
                { value: "sonatype_nexus", label: "Sonatype Nexus Enterprise Repository" }
              ]
            },
            {
              qid: "image_signing",
              label: "Are production container images digitally signed before deployment?",
              type: "single_choice",
              required: true,
              options: [
                { value: "sigstore_cosign", label: "Yes - digitally signed using Cosign / Sigstore tooling" },
                { value: "docker_trust", label: "Yes - signed using Docker Content Trust (DCT / Notary)" },
                { value: "no_signing", label: "No - custom images are not cryptographically signed" }
              ]
            },
            {
              qid: "base_image_scans",
              label: "Are base Docker images scanned for vulnerabilities in the registry?",
              type: "yes_no",
              required: true
            },
            {
              qid: "registry_access_policy",
              label: "Access scope control policy governing the package registry",
              type: "dropdown",
              required: true,
              options: [
                { value: "private_mfa_gated", label: "Private - strict IAM login gating with MFA enforced" },
                { value: "private_subnet_only", label: "Private - accessible only from within designated VPC limits" },
                { value: "public_open_pull", label: "Public - open read/pull access globally, write restricted" }
              ]
            }
          ]
        },
        {
          id: "sec_pipeline_access",
          title: "3. Environment Access & GitOps Delivery",
          description: "Examine continuous delivery tools, environment variables, and logical networks.",
          questions: [
            {
              qid: "deployment_tool",
              label: "Continuous Delivery (CD) mechanism in use",
              type: "dropdown",
              required: true,
              options: [
                { value: "gitops_argocd", label: "GitOps deployment engines (ArgoCD, FluxCD)" },
                { value: "ansible_terraform", label: "Ansible / Terraform remote infrastructure deployment" },
                { value: "custom_ssh_scp", label: "Custom SSH / SCP execution scripts from runners" },
                { value: "cloud_native_deploy", label: "AWS CodeDeploy / Azure DevOps Release pipelines" }
              ]
            },
            {
              qid: "pipeline_secrets_exposure",
              label: "Are production deployment secrets injected directly into pipeline environments?",
              type: "yes_no",
              required: true
            },
            {
              qid: "staging_prod_isolation",
              label: "Are staging and production deployment environments logically isolated at the network layer?",
              type: "yes_no",
              required: true
            },
            {
              qid: "gitops_pull_mode",
              label: "Is deployment executed in dynamic pull-mode (Argo/Flux) vs push-mode (direct runner CLI execution)?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_pipeline_safeguards",
          title: "4. Deployment Safeguards & Gated Approvals",
          description: "Verify approval checkpoints, canary models, automated rollbacks, and configuration drifts.",
          questions: [
            {
              qid: "manual_gated_approvals",
              label: "Are production merges gated by mandatory secondary administrator approvals?",
              type: "yes_no",
              required: true
            },
            {
              qid: "canary_deployments",
              label: "Are code deployments routed via progressive canary rollouts (e.g. 5% target traffic baseline first)?",
              type: "yes_no",
              required: true
            },
            {
              qid: "automated_rollback",
              label: "Do system monitors trigger automated branch rollbacks when post-deploy error rates spike?",
              type: "yes_no",
              required: true
            },
            {
              qid: "drift_detection",
              label: "Is there active drift detection to flag manual out-of-band updates on production hosts?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_pipeline_auditor_access",
          title: "5. Auditor Access & Credential Setup",
          description: "Detail setup variables, read dashboard setups, YAML samples, and API allocations for auditors.",
          questions: [
            {
              qid: "pipeline_access_method",
              label: "Access route allocated to the scoping team for pipeline review",
              type: "dropdown",
              required: true,
              options: [
                { value: "read_only_ci_invite", label: "Read-only access invitation to CI/CD dashboard suites" },
                { value: "config_file_vault", label: "Pipeline configuration files (.yml / .json) shared via secure vault" },
                { value: "screen_share_dev", label: "Interactive configuration review with CI/CD system administrators" }
              ]
            },
            {
              qid: "read_only_dashboard",
              label: "Will read-only dashboards for runner execution parameters be provisioned?",
              type: "yes_no",
              required: true
            },
            {
              qid: "sample_yaml_provided",
              label: "Are sample deployment deployment scripts made available for configuration review?",
              type: "yes_no",
              required: true
            },
            {
              qid: "temporary_pat_token",
              label: "Will a temporary programmatic Personal Access Token (PAT) be assigned to testers?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_pipeline_policy",
          title: "6. Policy Enforcement & Audit Trail",
          description: "Examine automation rules, execution log history, alert logs, and compliance scales.",
          questions: [
            {
              qid: "policy_as_code",
              label: "Are pipeline configurations governed by automated Policy-as-Code standards (e.g. OPA / Conftest)?",
              type: "yes_no",
              required: true
            },
            {
              qid: "pipeline_execution_logs",
              label: "Storage duration for runner build log records",
              type: "dropdown",
              required: true,
              options: [
                { value: "retention_14_days", label: "14 days or less" },
                { value: "retention_90_days", label: "90 days pipeline execution audit history" },
                { value: "retention_compliance_archived", label: "Archived long-term in audit logs vault" }
              ]
            },
            {
              qid: "auditor_alert_triggers",
              label: "Do system monitors trigger Slack or PagerDuty alerts when pipelines fail on vulnerability blocks?",
              type: "yes_no",
              required: true
            },
            {
              qid: "regulatory_audit_standard",
              label: "Primary pipeline compliance benchmark target",
              type: "dropdown",
              required: true,
              options: [
                { value: "slsa_level_3", label: "SLSA (Supply-chain Levels for Software Artifacts) Level 3" },
                { value: "ssdf_nist", label: "NIST Secure Software Development Framework (SSDF)" },
                { value: "internal_custom_pipeline_rules", label: "Internal custom baseline rules only" }
              ]
            }
          ]
        },
        {
          id: "sec_pipeline_scoping",
          title: "7. Target Pipelines & Config Files",
          description: "List path configurations and pipeline git addresses in scope.",
          questions: [
            {
              qid: "scoping_methodologies",
              label: "Pipeline configurations & files",
              type: "target_builder",
              required: true
            },
            {
              qid: "target_pipeline_yamls",
              label: "File paths to target workflow YAML configurations (e.g. .github/workflows/deploy.yml)",
              type: "long_text",
              required: true
            },
            {
              qid: "third_party_integrations",
              label: "Are external SaaS deployment integrations (e.g. Sentry, Datadog hooks) in-scope for review?",
              type: "yes_no",
              required: true
            },
            {
              qid: "out_of_scope_runners",
              label: "Specify build environments or execution hosts that are strictly excluded from testing",
              type: "long_text",
              required: false
            }
          ]
        }
      ]
    }
  },
  {
    name: "10. Fintech Compliance Pentest (PCI-DSS & SOC2)",
    templateName: "Fintech Compliance Pentest (PCI-DSS & SOC2)",
    schema: {
      sections: [
        {
          id: "sec_fintech_context",
          title: "1. Mandated Regulatory Frameworks",
          description: "Outline framework standards, cardholder segmentation, and data entry channels.",
          questions: [
            {
              qid: "regulatory_scope",
              label: "Primary target regulatory framework",
              type: "single_choice",
              required: true,
              options: [
                { value: "pci_dss_l1", label: "PCI-DSS Level 1 Audit (Cardholder Data Environment)" },
                { value: "soc2_type2", label: "SOC2 Type II Attestation (Trust Services Criteria)" },
                { value: "hipaa_ephi", label: "HIPAA Security Rule (ePHI protection)" },
                { value: "iso27001_isms", label: "ISO 27001 ISMS Alignment" },
                { value: "fedramp_mod", label: "FedRAMP Moderate/High Authorization" }
              ]
            },
            {
              qid: "cde_isolation",
              label: "Is the Cardholder Data Environment (CDE) strictly segmented from general corporate networks?",
              type: "yes_no",
              required: true
            },
            {
              qid: "payment_flows",
              label: "How does the application capture payment card data?",
              type: "dropdown",
              required: true,
              options: [
                { value: "hosted_iframes", label: "Hosted Payment Fields / Iframes (e.g. Stripe Elements, Braintree v3 Web SDK)" },
                { value: "direct_api_pan", label: "Direct API submission to payment gateways (PAN/CVV processed inside app servers)" },
                { value: "native_pos_token", label: "Native tokenization hardware in a physical point-of-sale network" }
              ]
            },
            {
              qid: "chd_stored",
              label: "Is Primary Account Number (PAN) or sensitive cardholder data stored inside internal databases?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_fintech_crypto",
          title: "2. Payment Processors & Crypto Controls",
          description: "Detail processor integrations, rest encryption, and minimum TLS protocols.",
          questions: [
            {
              qid: "payment_processors",
              label: "Integrated payment processing platforms",
              type: "multi_choice",
              required: true,
              options: [
                { value: "stripe_api", label: "Stripe API billing engines" },
                { value: "adyen_enterprise", label: "Adyen Enterprise processing" },
                { value: "braintree_paypal", label: "Braintree / PayPal API Gateway" },
                { value: "authorizenet", label: "Authorize.Net legacy gateway integration" },
                { value: "chase_paymentech", label: "Chase Paymentech corporate terminal routing" },
                { value: "direct_ach_swift", label: "Direct banking rail routing (ACH, SWIFT, FedNow)" }
              ]
            },
            {
              qid: "cde_db_encryption",
              label: "Are databases storing customer transactional records fully encrypted at rest?",
              type: "yes_no",
              required: true
            },
            {
              qid: "transit_tls",
              label: "Minimum TLS protocol version supported on public CDE gateways",
              type: "single_choice",
              required: true,
              options: [
                { value: "tls13_only", label: "TLS 1.3 only (Modern secure baseline)" },
                { value: "tls12_min", label: "TLS 1.2 minimum standard" },
                { value: "legacy_tls_allowed", label: "Legacy TLS 1.1 or 1.0 supported for compatibility reasons" }
              ]
            },
            {
              qid: "key_management_procedure",
              label: "Cryptographic key rotation governance standard",
              type: "dropdown",
              required: true,
              options: [
                { value: "kms_auto_rotate", label: "Automated rotation annually via cloud KMS" },
                { value: "manual_dual_custody", label: "Manual key rotation under dual-custody parameters" },
                { value: "no_formal_key_rotation", label: "No documented cryptographic key rotation guidelines" }
              ]
            }
          ]
        },
        {
          id: "sec_fintech_scanning",
          title: "3. Audit Logging & Vulnerability Scans",
          description: "Review log integrity verification, ASV schedules, and file monitors.",
          questions: [
            {
              qid: "log_encryption",
              label: "Are all compliance-relevant logs encrypted and integrity-validated (e.g. WORM storage)?",
              type: "yes_no",
              required: true
            },
            {
              qid: "asv_scans",
              label: "Do you perform quarterly Approved Scanning Vendor (ASV) external vulnerability scans?",
              type: "yes_no",
              required: true
            },
            {
              qid: "fim_active",
              label: "Is File Integrity Monitoring (FIM) active on production CDE servers?",
              type: "yes_no",
              required: true
            },
            {
              qid: "siem_realtime_alerts",
              label: "Are transactional logs analyzed by real-time SIEM systems for anomaly patterns?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_fintech_auditor",
          title: "4. Auditor Access & Payment Testing Environment",
          description: "Detail test transaction cards, sandbox credentials, mock APIs, and safelist coordinates.",
          questions: [
            {
              qid: "testing_payment_tokens",
              label: "Will test credit card numbers and mock payment tokens be provided to auditors?",
              type: "yes_no",
              required: true
            },
            {
              qid: "fintech_staging_credentials",
              label: "Auditor testing profile provisioning method",
              type: "dropdown",
              required: true,
              options: [
                { value: "pre_registered_pci", label: "Pre-registered sandbox customer and merchant profiles" },
                { value: "live_staging_registration", label: "Self-registration allowed within designated staging environments" },
                { value: "api_only_pci_tokens", label: "Direct API credentials with mock verification tokens shared" }
              ]
            },
            {
              qid: "api_mocks_available",
              label: "Are external payment processor APIs mocked/stubbed in the staging network?",
              type: "yes_no",
              required: true
            },
            {
              qid: "whitelist_regulatory_ips",
              label: "Will auditor network coordinates be whitelisted on corporate intrusion logs?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_fintech_users",
          title: "5. Identity & Access Control in CDE",
          description: "Analyze multi-factor setups, length metrics, timeout bounds, and admin limits inside the cardholder zone.",
          questions: [
            {
              qid: "cde_mfa",
              label: "Is Multi-Factor Authentication (MFA) strictly enforced for all administrator sessions in the CDE?",
              type: "yes_no",
              required: true
            },
            {
              qid: "password_complexity_compliance",
              label: "Are active password rules configured to enforce PCI-mandated length and character complexity?",
              type: "yes_no",
              required: true
            },
            {
              qid: "idle_session_termination",
              label: "Is CDE user session idle termination configured at 15 minutes or less?",
              type: "yes_no",
              required: true
            },
            {
              qid: "privileged_roles_limit",
              label: "Total count of user identities with write-permissions in the CDE database tier",
              type: "single_choice",
              required: true,
              options: [
                { value: "highly_restricted", label: "Under 5 user accounts (Optimal profile)" },
                { value: "moderately_restricted", label: "5 to 15 user accounts" },
                { value: "broad_access", label: "Over 15 user identities with administrative privileges" }
              ]
            }
          ]
        },
        {
          id: "sec_fintech_resilience",
          title: "6. Database Backup & Disaster Recovery",
          description: "Analyze backup cryptography, drill milestones, log lifetimes, and command protocols.",
          questions: [
            {
              qid: "backup_encryption",
              label: "Are database backups cryptographically encrypted and stored in offsite zones?",
              type: "yes_no",
              required: true
            },
            {
              qid: "dr_test_frequency",
              label: "Disaster Recovery (DR) data restoration test frequency",
              type: "single_choice",
              required: true,
              options: [
                { value: "dr_bi_annually", label: "Semi-annually (Every 6 months or more frequently)" },
                { value: "dr_annually", label: "Annually (Standard threshold requirement)" },
                { value: "dr_no_test", label: "No regular scheduled disaster recovery restoration testing conducted" }
              ]
            },
            {
              qid: "log_retention_period",
              label: "Mandatory transactional event log archive retention period",
              type: "dropdown",
              required: true,
              options: [
                { value: "retention_1_year", label: "1 year (PCI-DSS minimum standard)" },
                { value: "retention_3_years", label: "3 years" },
                { value: "retention_regulatory_7_years", label: "7 years (Regulatory/audit legal retention standard)" }
              ]
            },
            {
              qid: "incident_command_structure",
              label: "Is there a designated emergency coordinator team defined for rapid transaction freeze?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_fintech_scoping",
          title: "7. CDE In-Scope Targets",
          description: "Register CDE hosting regions, microservice endpoints, and external gateway IPs.",
          questions: [
            {
              qid: "scoping_methodologies",
              label: "CDE Gateways & Payment Endpoints",
              type: "target_builder",
              required: true
            },
            {
              qid: "cde_domains_ips",
              label: "Specify the exact base domains and public IP addresses comprising the CDE perimeter",
              type: "long_text",
              required: true
            },
            {
              qid: "third_party_payment_apis",
              label: "Are secondary third-party APIs (e.g. credit-rating bureaus, identity checks) in-scope?",
              type: "yes_no",
              required: true
            },
            {
              qid: "regulatory_exemptions",
              label: "Specify any payment modules or systems that have been granted official compliance scope exemptions",
              type: "long_text",
              required: false
            }
          ]
        }
      ]
    }
  }
];

const TemplateDevAutofill: React.FC<TemplateDevAutofillProps> = ({
  editingTemplate,
  setEditingTemplate,
  setTemplateName,
  schemaEditor
}) => {
  const isDev = process.env.NODE_ENV === "development" || process.env.REACT_APP_SHOW_DEV_TOOLS === "true";
  const [isExpanded, setIsExpanded] = useState(false);
  const [devPanelPos, setDevPanelPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const dragStartCoords = useRef({ x: 0, y: 0 });
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragTotalDistance = useRef(0);

  const handlePointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    const isGrip = target.closest(".drag-handle");
    const isBubble = !isExpanded && target.closest(".dev-bubble");

    if (isGrip || isBubble) {
      setIsDragging(true);
      dragStartCoords.current = { x: e.clientX, y: e.clientY };
      dragOffset.current = {
        x: e.clientX - devPanelPos.x,
        y: e.clientY - devPanelPos.y
      };
      dragTotalDistance.current = 0;
      target.setPointerCapture(e.pointerId);
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => {
      const dx = e.clientX - dragStartCoords.current.x;
      const dy = e.clientY - dragStartCoords.current.y;
      dragTotalDistance.current = Math.sqrt(dx * dx + dy * dy);

      setDevPanelPos({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y
      });
    };

    const handlePointerUp = (e: PointerEvent) => {
      setIsDragging(false);
      try {
        const target = e.target as HTMLElement;
        target.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Safe check
      }

      if (dragTotalDistance.current < 5) {
        setIsExpanded(prev => !prev);
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging, devPanelPos]);

  if (!isDev) return null;

  return (
    <div
      style={{
        transform: `translate(${devPanelPos.x}px, ${devPanelPos.y}px)`,
        touchAction: "none"
      }}
      onPointerDown={handlePointerDown}
      className="fixed bottom-6 right-6 z-[999999] select-none shrink-0"
    >
      {!isExpanded ? (
        /* Collapsed Mini-Bubble FAB */
        <div
          className={`dev-bubble w-9 h-9 bg-gradient-to-tr from-indigo-600 to-violet-500 rounded-full flex items-center justify-center cursor-grab shadow-lg border border-indigo-400/30 hover:scale-105 active:scale-95 transition duration-150 relative ${
            isDragging ? "cursor-grabbing ring-2 ring-indigo-500/30" : ""
          }`}
          title="🛠️ Templates Dev Wizard (Click to open, Drag to move)"
        >
          <Wrench size={13} className="text-white animate-pulse" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full ring-1 ring-white" />
        </div>
      ) : (
        /* Sleek, Ultra-Compact open Selector Widget */
        <div
          className={`bg-white/95 backdrop-blur-md border border-indigo-150 p-2 rounded-xl shadow-2xl w-[220px] flex flex-col gap-1.5 transition-all duration-200 ${
            isDragging ? "ring-2 ring-indigo-500/20 shadow-indigo-100/50" : ""
          }`}
        >
          {/* Header Drag Bar */}
          <div className="flex items-center gap-1 pb-1 border-b border-indigo-50/60 select-none shrink-0">
            <div
              className={`drag-handle text-indigo-300 font-extrabold text-[10px] tracking-tight flex items-center justify-center mr-0.5 select-none ${
                isDragging ? "cursor-grabbing" : "cursor-grab"
              }`}
              title="Drag to reposition panel"
            >
              ⣿
            </div>
            <div className="flex items-center justify-center w-3.5 h-3.5 rounded bg-indigo-600 text-white shadow-sm shrink-0">
              <Wrench size={8} className="text-white" />
            </div>
            <div className="flex-1">
              <span className="font-extrabold text-indigo-950 text-[8.5px] leading-none block">🛠️ Templates Dev</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
              }}
              className="w-4.5 h-4.5 p-0.5 flex items-center justify-center rounded text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition shrink-0"
              title="Minimize panel"
            >
              <Minimize2 size={8} />
            </button>
          </div>

          {/* Selector Dropdown */}
          <div className="relative">
            <select
              defaultValue=""
              onChange={(e) => {
                const idx = e.target.value;
                if (idx !== "") {
                  const sampleIdx = Number(idx);
                  const sample = TEMPLATE_AUTOFILL_SAMPLES[sampleIdx];
                  if (sample) {
                    // Trigger edit mode if not already editing
                    if (!editingTemplate) {
                      setEditingTemplate({ id: undefined });
                    }

                    // Prepopulate values
                    setTemplateName(sample.templateName);
                    schemaEditor.resetEditor(sample.schema, 0);

                    toast.success(`⚡ Load Sample: Loaded "${sample.templateName}"!`);
                    e.target.value = "";
                  }
                }
              }}
              className="w-full h-6.5 pl-1.5 pr-5 bg-white border border-indigo-150 rounded-lg text-[9px] font-bold text-indigo-900 focus:outline-none focus:border-indigo-500 hover:border-indigo-300 transition cursor-pointer shadow-sm appearance-none"
            >
              <option value="" disabled className="text-indigo-400">-- Choose Template (10) --</option>
              {TEMPLATE_AUTOFILL_SAMPLES.map((sample, idx) => (
                <option key={idx} value={idx}>
                  {idx + 1}. {sample.name}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-1.5 flex items-center pointer-events-none text-indigo-500">
              <ChevronDown size={8} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateDevAutofill;
